import Linkify from 'linkify-react';
import { Opts } from 'linkifyjs';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { truncateAccurately } from 'shared/helpers';

import { Button, Link } from '../../Components/designSystem';

type Props = {
    text?: string;
    symbolsLimit?: number;
    expandable?: boolean;
}

const TextWithParsedLinks = ({ text = '', symbolsLimit, expandable }: Props) => {
    const [ expanded, setExpanded ] = useState(false);

    const shouldTruncate = !expanded && Boolean(symbolsLimit && text.length > symbolsLimit);
    const truncatedText = shouldTruncate && symbolsLimit && !expanded 
        ? truncateAccurately(text, symbolsLimit)
        : text;

    const renderLink: Opts['render'] = useCallback(({ attributes, content }) => {
        if (isTruncatedLinkPart(text, truncatedText, content)) {
            return <></>;
        }

        return (
            <Link
                { ...attributes }
                url={ attributes.href }
                style={{ color: 'inherit', textDecoration: 'underline' }}
            >
                { content }
            </Link>
        );
    }, [ truncatedText, text ]);

    if (!text) {
        return null;
    }

    return (
        <div style={{ fontSize: '14px' }}>
            <Linkify options={{ target: '_blank', render: renderLink }}>
                { truncatedText }
            </Linkify>
            { expandable && (shouldTruncate || expanded) 
                ? <ExpandControl expanded={ expanded }
                    setExpanded={ setExpanded } />
                : null
            }
        </div>
    );
};

const ExpandControl = ({ expanded, setExpanded }: { expanded: boolean, setExpanded: (value: boolean) => void }) => {
    const { t } = useTranslation('common');

    const handleClick = () => setExpanded(!expanded);

    return (
        <Button color="link"
            label={ expanded ? t('textShowLess') : t('textShowMore') }
            onClick={ handleClick }
        />
    );
};

function isTruncatedLinkPart(text: string, truncatedText: string, linkText: string) {
    return text.length !== truncatedText.length &&
        truncatedText.endsWith(`${ linkText }...`);
}

export default TextWithParsedLinks;
